import { ref, watch } from '@vue/composition-api'
import store from '@/store'
import { useRouter } from '@core/utils/utils'
import { useLambda } from '@/views/habit/awsLambda'
import { oid, date } from '@/helpers/mongoHelpers'
import useCommonTodo from '@/views/apps/todo/useCommonTodo'

export default function useTaskHandler(props, emit) {
  // ------------------------------------------------
  // taskLocal
  // ------------------------------------------------
  const taskLocal = ref(JSON.parse(JSON.stringify(props.task.value)))
  const originalCompletedState = ref(false)
  const { router } = useRouter()

  const userData = store.state?.userStore?.userData

  const compressor = ref(null)
  const imgData = ref({})

  const commitmentFunctionality = JSON.parse(localStorage.getItem("clientData") || '{}').commitment_functionality
  const showModal = ref(false)
  const docSelector = ref(null)
  const okClicked = ref(false)
  const docEvidence = ref(null)
  const imgEvidence = ref(null)
  const disabledImg = ref(false)
  const disabledDoc = ref(false)
  const deleteEvidence = ref(false)
    const { updateItem, updateItems } = useLambda()
  const { uploadFileToS3 } = useCommonTodo()

  const commitmentsToUpdate = ref([])
  const commitmentsToDelete = ref([])
  
  const resetTaskLocal = () => {
    taskLocal.value = JSON.parse(JSON.stringify(props.task.value))
    originalCompletedState.value = taskLocal.value.completed
    imgData.value = {}
  }

  watch(props.task, () => {
    resetTaskLocal()
  })

  const onSubmit = async () => {
    const taskData = JSON.parse(JSON.stringify(taskLocal))

    // * If event has id => Edit Event
    // Emit event for add/update event
    if (props.task.value._id || props.taskIndex.value !== -1) {
      const action = taskData.value.completed && !originalCompletedState.value ? "completeFromForm" : ""

      // Save evidence of completed commit in AWS and DB
      const evidenceFile = imgEvidence.value ? imgEvidence.value : docEvidence.value
      const type = imgEvidence.value ? 'img' : 'doc'
      if(evidenceFile) {
        try {
          const key = await saveEvidenceAWS(evidenceFile)
          taskData.value.evidenceKey = { key, type }
        } catch (err) {
          console.error(err)
        }
      }

      // Delete evidence of commitment in DB
      if (deleteEvidence.value) {
        delete taskData.value.evidenceKey
        taskData.value.evidenceKey_unset = true
      }
      emit('update-task', taskData.value, props.taskIndex.value, action, commitmentsToUpdate.value, commitmentsToDelete.value)
    } else {
      if (imgData.value.fileInfo) taskData.value.imgData = imgData.value
      emit('add-task', taskData.value)
    }

    // Update commitments
    if (commitmentsToUpdate.value.length) {
      commitmentsToUpdate.value.forEach(e => {
        const query = { _id: oid(e._id) }
        const payload = {
          title: e.title || '',
          assignee: oid(e.assignee),
          dueDate: e.dueDate ? date(new Date(`${e.dueDate.slice(0, 10)} 12:00:00`)) : null,
          completed: e.completed || false,
        }
        const action = { $set: payload }
  
        try {
          updateItem({ collection: 'commitment', query, action })
        } catch (error) {
          console.log(error)
        }
      })
    }

    // Delete improvements
    if (commitmentsToDelete.value.length) {
      const query = { _id: { $in: commitmentsToDelete.value.map(e => oid(e._id)) } }
      const action = { $set: { deleted: true } }
      
      try {
        updateItems({ collection: 'commitment', query, action })
      } catch (error) {
        console.log(error)
      }
    }

    // Close sidebar
    emit('update:is-task-handler-sidebar-active', false)
  }

  const uploadImage = () => {
    compressor.value.$el.click()
  }

  const getImgCompressed = (obj) => {
    imgData.value = {
      fileInfo: obj.compressed,
      destinationFolder: `${userData.client.$oid}/improvements`
    }
  }

  const resetVariables = () => {
    // Set toggle task handler sidebar in store to false
    store.commit('app-todo/SET_TOGGLE_TASK_HANDLER_SIDEBAR', false)
    docEvidence.value = null
    imgEvidence.value = null
    disabledImg.value = false
    disabledDoc.value = false
    okClicked.value = false
    deleteEvidence.value = false
  }

  const goToOrigin = (origin, originId) => {
    switch (origin) {
      case 'event':
        router.push({ name: "apps-calendar", query: { originId } })
        break;
      case 'problem_solving':
        router.push({ name: "apps-problem-solving", query: { originId } })
        break;
      case 'confirmation':
        router.push({ path: `/habit/confirmation/view/${originId}` })
        break;
      default:
        break;
    }
  }

  const changeTaskStatus = () => {
    if(commitmentFunctionality) {
      (!taskLocal.value.completed || taskLocal.value.evidenceKey)
        ? showModal.value = true
        : taskLocal.value.completed = !taskLocal.value.completed
    } else {
      taskLocal.value.completed = !taskLocal.value.completed
    }
  }

  const handleModalHide = () => {
    if(!okClicked.value) resetVariables() 
  }

  const handleModalOk = () => {
    if(taskLocal.value.completed) deleteEvidence.value = true
    taskLocal.value.completed = !taskLocal.value.completed
    okClicked.value = true
  }
  
  const uploadImageOrFile = (type) => {
    type === 'img'? uploadImage() : docSelector.value.click()
  }

  const getDocEvidence = (e) => {
    docEvidence.value = e.target.files[0]
    disabledImg.value = true
  }

  const getImgEvidence = (obj) => {
    imgEvidence.value = obj.compressed
    disabledDoc.value = true
  }

  const saveEvidenceAWS = (file) => {
    const destinationFolder = `${userData.client.$oid}/evidenceCommitments`;
    return new Promise((resolve, reject) => {
      uploadFileToS3(file, destinationFolder)
        .then((key) => resolve(key))
        .catch((err) => reject(err))
  })
}

  return {
    taskLocal,
    resetTaskLocal,
    onSubmit,
    uploadImage,
    getImgCompressed,
    compressor,
    imgData,
    resetVariables,
    goToOrigin,
    showModal,
    changeTaskStatus,
    handleModalOk,
    uploadImageOrFile,
    docEvidence,
    getDocEvidence,
    docSelector,
    handleModalHide,
    getImgEvidence,
    imgEvidence,
    disabledImg,
    disabledDoc,
    deleteEvidence,
    commitmentsToUpdate,
    commitmentsToDelete,
  }
}
